// CD.AutoSave - utilities for working with objects that are automatically saved when changed
import S from 's-js';
import * as _ from 'underscore';
import { Client } from './Ajax';
import { RequestQueue } from './requestQueue';
import { DraftObject } from './draftSignals';

const DEFAULT_SAVE_DELAY = 300;

/**
 * An AutoSaver debounces and serializes calls to a given save function when triggered by the
 * given trigger.
 * @param trigger Trigger that indicates that a save is needed.
 * @param save Function that performs a single save.
 * @param delay Debounce delay. A save won't be enqueued until this many ms of quiet after the
 * trigger has fired.
 * @param onchanges Should a save be enqueued immediately (false) or only after the trigger
 * has fired (true). Defaults to true.
 */
export function AutoSaver<T>(trigger : () => void, save : () => PromiseLike<T>, delay = DEFAULT_SAVE_DELAY, onchanges = true) {
    const
        queue = new RequestQueue(() => save()),
        debouncedSaver = _.debounce(Save, delay);

    // Note: debouncedSaver will still run even if this computation is disposed, since it's been queued by _.debounce()
    S.on(trigger, debouncedSaver, null, onchanges);

    return { Save };

    function Save() {
        queue.enqueueRequest();
        return queue.mostRecentRequest();
    }
}

/**
 * Save the given draft object using the given client.
 */
export function SaveDraftObject<T, U>(draft : DraftObject<T>, client : Client<T, U>) {
    const
        checkpoint = draft.methods.checkpoint(),
        req = client.Put(draft.object);

    return req.then(() => {
        const resp = req.Response();
        if (resp?.UsedCache) {
            // if we updated the cache directly, then revert our changes, so that we show latest from server
            checkpoint.revert();
        } else {
            // if we didn't use cache, then assume our changes stuck, since the request succeeded, and commit them
            checkpoint.commit();
        }
        return resp;
    });
}

/**
 * A DraftAutoSaver watches for changes in the given DraftObject and fires an http PUT request
 * to the given client when a change is detected. If the PUT is successful, DraftAutoSaver triggers 
 * a commit on the draft object to update the underlying model object with the newly saved values.
 *
 * In order to avoid spamming the server, DraftAutoSaver waits for 300 ms of quiet before sending out
 * the PUT request.
 *
 * To avoid a situation where save requests reach the server out of order, DraftAutoSaver serializes
 * its requests, not sending the next until it hears that the former completed.
 *
 * @param draft The draft object to be changed
 * @param client Client to send the PUT request
 */
export function DraftAutoSaver<T, U>(draft : DraftObject<T>, client : Client<T, U>) {
    return AutoSaver(draft.methods.onWrite, () => SaveDraftObject(draft, client));
}

/**
 * AutoSavingDraftObject is a short-hand method whidh constructs a DraftObject from the given object
 * and wires up a DraftAutoSaver to it. It saves a few lines of code in cases where the client does
 * not need access to the saver's interface.
 * @param obj The object from which to create the draft
 * @param client Client to send the PUT request
 * @param props Specific list of properties to watch. Defaults to all.
 */
export function AutoSavingDraftObject<T extends object>(obj : T, client : Client<T, unknown>, props? : string[]) : T {
    const draft = DraftObject(obj, props);
    DraftAutoSaver(draft, client);
    return draft.object;
}



// WEBPACK FOOTER //
// ./lib/autoSave.ts